import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Award, CalendarDays, ClipboardList, FileText, Heart, Stethoscope, Dumbbell } from "lucide-react";

// Helper: format date ISO to RO "17 iunie 2026"
const formatDateRo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const luni = [
    "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
    "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie",
  ];
  return `${d.getDate()} ${luni[d.getMonth()]} ${d.getFullYear()}`;
};

const CONCLUZIE_LABELS = {
  APT: { label: "APT antrenamente și competiții", cls: "bg-emerald-600 text-white" },
  APT_SUPRAVEGHERE: { label: "APT antrenamente și competiții sub supraveghere medicală", cls: "bg-amber-600 text-white" },
  APT_INDIVIDUALIZAT: { label: "APT antrenamente individualizate sub supraveghere", cls: "bg-orange-600 text-white" },
  APT_CONDITIONAT: { label: "APT condiționat (legacy)", cls: "bg-orange-500 text-white" },
  INAPT_TEMPORAR: { label: "INAPT temporar", cls: "bg-red-500 text-white" },
  INAPT: { label: "INAPT (legacy)", cls: "bg-slate-900 text-white" },
};

const Field = ({ label, value }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className="text-sm font-medium">{String(value)}</span>
    </div>
  );
};

const AtelierSection = ({ title, icon: Icon, data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  // Filtrăm cheile lipsite de valoare reală
  const entries = Object.entries(data).filter(
    ([k, v]) => v !== "" && v !== null && v !== undefined && v !== false && k !== "completat" && k !== "data_completare"
  );
  if (entries.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-primary">
          <Icon className="w-4 h-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {entries.map(([k, v]) => {
            // Pentru obiecte/array, afișăm scurt
            let display = v;
            if (typeof v === "object") {
              if (Array.isArray(v)) {
                if (v.length === 0) return null;
                display = `${v.length} intrări`;
              } else {
                const keys = Object.keys(v).filter((kk) => v[kk] !== "" && v[kk] !== false && v[kk] !== null);
                if (keys.length === 0) return null;
                display = JSON.stringify(v).slice(0, 80) + "...";
              }
            } else if (typeof v === "boolean") {
              display = v ? "Da" : "Nu";
            }
            return <Field key={k} label={k.replace(/_/g, " ")} value={display} />;
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const FisaCompletaDialog = ({ fisa, open, onClose }) => {
  if (!fisa) return null;
  const dp = fisa.date_personale || {};
  const is = fisa.info_sportive || {};
  const referat = fisa.referat || {};
  const concluzieInfo = referat.concluzie ? CONCLUZIE_LABELS[referat.concluzie] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="fisa-completa-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-3 text-xl">
            <ClipboardList className="w-6 h-6 text-emerald-600" />
            Fișă medicală — Viză #{fisa.numar_viza}
            <Badge className="bg-emerald-600 text-white ml-2">
              <CalendarDays className="w-3 h-3 mr-1" />
              {formatDateRo(fisa.date)}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Snapshot al fișei la momentul generării numărului de ordine #{fisa.numar_viza}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-3" data-testid="fisa-scroll-container">
          <div className="space-y-4">
            {/* Concluzie referat */}
            {concluzieInfo && (
              <div className={`p-4 rounded-lg ${concluzieInfo.cls} flex items-center gap-3`}>
                <Award className="w-6 h-6" />
                <div>
                  <p className="text-xs uppercase opacity-80">Calificativ final</p>
                  <p className="text-lg font-semibold">{concluzieInfo.label}</p>
                </div>
              </div>
            )}

            {/* Date pacient + sport */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-primary">
                  <FileText className="w-4 h-4" />
                  Date pacient (la momentul vizei)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Field label="Nume" value={dp.nume} />
                  <Field label="Prenume" value={dp.prenume} />
                  <Field label="Data nașterii" value={dp.data_nasterii} />
                  <Field label="Sex" value={dp.sex} />
                  <Field label="CNP" value={dp.cnp} />
                  <Field label="Telefon" value={dp.telefon} />
                  <Field label="Localitate" value={dp.localitate} />
                  <Field label="Sport practicat" value={is.sport_practicat} />
                  <Field label="Club sportiv" value={is.club_sportiv} />
                  <Field label="Post / Categorie" value={is.post_categorie} />
                  <Field label="Antrenor" value={is.antrenor} />
                  <Field label="Antrenamente / săpt." value={is.antrenamente_saptamana} />
                </div>
              </CardContent>
            </Card>

            <AtelierSection title="Atelier 1 — Compoziție corporală & forță & postură" icon={Dumbbell} data={fisa.atelier_1} />
            <AtelierSection title="Atelier 2 — Cardiovascular & Ruffier & Astrand" icon={Heart} data={fisa.atelier_2} />
            <AtelierSection title="Atelier 3 — ECG SCHILLER" icon={Heart} data={fisa.atelier_3} />
            <AtelierSection title="Atelier 4 — Examen clinic" icon={Stethoscope} data={fisa.atelier_4} />

            {/* Referat — recomandări / observații */}
            {(referat.recomandari?.length > 0 || referat.observatii?.length > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-primary">
                    <FileText className="w-4 h-4" />
                    Referat medical
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {referat.recomandari?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Recomandări</p>
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        {referat.recomandari.map((r) => (
                          <li key={r.id || r.text}>{r.text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {referat.observatii?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Observații</p>
                      <ul className="text-sm space-y-1 list-disc list-inside">
                        {referat.observatii.map((o) => (
                          <li key={o.id || o.text}>{o.text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
